import { useEffect, useMemo, useRef, useState } from 'react'
import { useProductPageContext } from 'src/components/restructure/product/contexts/product-context'
import { subscribe } from 'src/components/restructure/product/productEvents'
import { InformationIcon } from 'src/components/Icons/InformationIcon'
import { checkEnviromentIsB2B } from 'src/utils/checkEnviroment'
import { getStandardObject } from 'src/utils/amplitude/getStandardObject'
import { getFeatureObject } from 'src/utils/amplitude/getFeatureObject'
import { InView } from 'react-intersection-observer'
import { sendProductInformationSelectedEvent } from 'src/utils/amplitude/sendProductInformationSelectedEvent'
import { getProductQueryObject } from 'src/utils/amplitude/useProductObject'
import { dispatchAmplitudeEvent } from 'src/utils/amplitude'
import { useGlobalContext } from 'src/components/contexts/GlobalContext'

import { PickupModal } from './PickupModal'
import { FreightSkeleton } from './components/FreightSkeleton'
import { FreightDetailsBox } from './components/FreightDetailsBox'
import { NextDayDelivery } from './components/NextDayDelivery'
import { PickupDelivery } from './components/PickupDelivery'
import { getDeliveriesByType } from './utils/getDeliveriesByType'
import { formatDeliveryTypeName } from './utils/formatDeliveryTypeName'

export type DeliveryTypes = Array<{
  type: string
  value: number
  shippingEstimate: string
}>

const isB2B = checkEnviromentIsB2B()

export function FreightSimulation() {
  const {
    deliveryIsAvailable,
    isLoadingFreightSimulation,
    freightSimulationValue,
    selectedPickupPoint,
    currentSku,
  } = useProductPageContext()

  const { pageData } = useGlobalContext()
  const [isPickupModalOpen, setPickupModalOpen] = useState(false)

  const deliveryAlertRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!deliveryIsAvailable && isB2B) {
      return subscribe('addToCart', () => {
        if (!deliveryAlertRef?.current) {
          return
        }

        window.scroll({
          top: deliveryAlertRef?.current?.offsetTop - 118,
          behavior: 'smooth',
        })
      })
    }
  }, [deliveryIsAvailable])

  const nextDayDeliveries = useMemo(() => {
    return getDeliveriesByType('next-day', freightSimulationValue)
  }, [freightSimulationValue])

  const fastDeliveries = useMemo(() => {
    return getDeliveriesByType('fast', freightSimulationValue)
  }, [freightSimulationValue])

  const economicDeliveries = useMemo(() => {
    return getDeliveriesByType('economic', freightSimulationValue)
  }, [freightSimulationValue])

  const pickupDeliveries = useMemo(() => {
    return getDeliveriesByType('pick-up', freightSimulationValue)
  }, [freightSimulationValue])

  const showPickupDeliveries =
    (pickupDeliveries?.length > 0 &&
      (freightSimulationValue?.packages?.[0]?.pickupPoints?.recommend ?? [])
        ?.length > 0) ||
    freightSimulationValue?.packages?.[0]?.pickupPoints?.others?.length

  const resolvedPickupPoint =
    selectedPickupPoint ??
    freightSimulationValue?.packages?.[0]?.pickupPoints?.recommend?.[0] ??
    freightSimulationValue?.packages?.[0]?.pickupPoints?.others?.[0] ??
    null

  function getShippingMethodSummary() {
    const parts: string[] = []

    const visibleDeliveryTypes = [
      nextDayDeliveries?.[0]?.type,
      fastDeliveries?.[0]?.type,
      economicDeliveries?.[0]?.type,
    ]
      .filter(Boolean)
      .map((type) => formatDeliveryTypeName(type as string))

    if (visibleDeliveryTypes.length > 0) {
      parts.push(visibleDeliveryTypes.join(', '))
    }

    if (showPickupDeliveries) {
      if (resolvedPickupPoint) {
        parts.push(`Retire em [${resolvedPickupPoint.name}]`)
      }
    }

    return parts.join(', ')
  }

  function sendPromoFeatureClickEvent() {
    const standardObject = getStandardObject(pageData)
    const featureDetailsObject = getFeatureObject({
      locationOnPage: null,
      name: 'Consulta rnl',
      section: 'produto',
    })

    const productObject = getProductQueryObject(currentSku)

    sendProductInformationSelectedEvent({
      clickedElement: 'Consulta rnl',
      featureDetailsObject,
      productObject,
      standardObject,
    })
  }

  const handleClickOnStore = () => {
    sendPromoFeatureClickEvent()
    setPickupModalOpen(true)

    document.body.style.overflow = 'hidden'
  }

  function sendShippingEvent() {
    if (deliveryIsAvailable) {
      const eventData = {
        ...getStandardObject(pageData),
      }

      dispatchAmplitudeEvent({
        eventName: 'Shipping Consult',
        eventData: {
          ...eventData,
          'shipping method': getShippingMethodSummary(),
        },
      })

      return
    }

    const featureObject = getFeatureObject({
      section: 'simulação de frete',
      locationOnPage: 'pdp',
      name: 'produto indisponível',
    })

    const eventData = {
      ...getStandardObject(pageData),
      ...featureObject,
    }

    dispatchAmplitudeEvent({ eventName: 'Product Feature Viewed', eventData })
  }

  return (
    <>
      {!isLoadingFreightSimulation && freightSimulationValue && (
        <InView
          triggerOnce
          as="div"
          className={`w-full flex flex-col gap-7 mt-${
            deliveryIsAvailable ? 'lg' : '[2px]'
          }`}
          threshold={0.7}
          onChange={(inView) => {
            if (inView) {
              sendShippingEvent()
            }
          }}
        >
          <div
            data-testid="resume-simulation-shipping"
            className="w-full flex flex-col gap-7"
          >
            {deliveryIsAvailable ? (
              <>
                {nextDayDeliveries?.length > 0 && (
                  <NextDayDelivery
                    shippingEstimate={nextDayDeliveries[0].shippingEstimate}
                    value={nextDayDeliveries[0].value}
                  />
                )}
                {fastDeliveries?.length > 0 && (
                  <FreightDetailsBox deliveryData={fastDeliveries[0]} />
                )}
                {economicDeliveries?.length > 0 && (
                  <FreightDetailsBox deliveryData={economicDeliveries[0]} />
                )}
                {!!showPickupDeliveries && (
                  <PickupDelivery
                    selectedPickupPoint={resolvedPickupPoint}
                    handleClickOnStore={handleClickOnStore}
                  />
                )}
              </>
            ) : (
              <div
                ref={deliveryAlertRef}
                className="text-restructure-error font-inter text-xs flex items-center p-4 pb-[18px] bg-[#FEC9CB66]"
              >
                <InformationIcon color="#AB0009" width={24} height={24} />
                <p className="ml-3 mr-2 text-sm leading-[21px] font-regular">
                  Este produto não está disponível para entrega na região
                  informada.
                </p>
              </div>
            )}
          </div>
          {(!!freightSimulationValue?.packages?.[0]?.pickupPoints?.recommend
            ?.length ||
            !!freightSimulationValue?.packages?.[0]?.pickupPoints?.others
              ?.length) && (
            <PickupModal
              isOpen={isPickupModalOpen}
              setOpenModal={setPickupModalOpen}
              pickupPoints={freightSimulationValue.packages[0].pickupPoints}
            />
          )}
        </InView>
      )}
      {isLoadingFreightSimulation && (
        <div
          className="w-full flex flex-col gap-7 mt-lg"
          data-testid="skeleton"
        >
          <FreightSkeleton isReverse={false} />
          <FreightSkeleton isReverse={false} />
          <FreightSkeleton isReverse />
        </div>
      )}
    </>
  )
}
